import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CarListingsAPI from '../../api/wgpt/carListings';
import { throwErrorMessage } from '../utils/api';
import { DirectUpload } from 'activestorage';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';

// Photos/videos go straight to storage when direct uploads are enabled on the
// installation, and only the blob signed id is sent; otherwise the raw file is
// sent as multipart.
const uploadPayload = async (listingId, file, rootGetters) => {
  if (rootGetters['globalConfig/get'].directUploadsEnabled) {
    const signedId = await new Promise((resolve, reject) => {
      new DirectUpload(file, CarListingsAPI.directUploadUrl(listingId), {
        directUploadWillCreateBlobWithXHR: setDirectUploadAuthHeaders,
      }).create((error, blob) =>
        error ? reject(error) : resolve(blob.signed_id)
      );
    });
    return { file: signedId };
  }
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

export const state = {
  records: [],
  meta: {
    current_page: 1,
    per_page: 24,
    total_count: 0,
    total_pages: 1,
    status_counts: {},
  },
  uiFlags: {
    isFetching: false,
    isFetchingMore: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUploadingPhoto: false,
    isRemovingPhoto: false,
    isUploadingVideo: false,
  },
};

export const getters = {
  getCarListings: $state => $state.records,
  getMeta: $state => $state.meta,
  getUIFlags: $state => $state.uiFlags,
  getCarListing: $state => id => {
    const carListing = $state.records.find(record => record.id === Number(id));
    return carListing || {};
  },
};

// Ignore out-of-order list responses: a slower earlier request (e.g. a broader
// filter) must not clobber the results of a newer one.
let listRequestId = 0;

export const actions = {
  get: async ({ commit }, params = {}) => {
    const { append = false, ...query } = params;
    const flag = append ? 'isFetchingMore' : 'isFetching';
    listRequestId += 1;
    const requestId = listRequestId;
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { [flag]: true });
    try {
      const { data } = await CarListingsAPI.get(query);
      if (requestId !== listRequestId) return;
      commit(
        append ? types.APPEND_WGPT_CAR_LISTINGS : types.SET_WGPT_CAR_LISTINGS,
        data.listings
      );
      commit(types.SET_WGPT_CAR_LISTING_META, data.meta);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      if (requestId === listRequestId) {
        commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { [flag]: false });
      }
    }
  },
  show: async ({ commit }, id) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await CarListingsAPI.show(id);
      commit(types.ADD_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isFetchingItem: false });
    }
    return null;
  },
  create: async ({ commit }, carListingData) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isCreating: true });
    try {
      const response = await CarListingsAPI.create(carListingData);
      commit(types.ADD_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isCreating: false });
    }
    return null;
  },
  update: async ({ commit }, { id, ...updateData }) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUpdating: true });
    try {
      const response = await CarListingsAPI.update(id, updateData);
      commit(types.EDIT_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  // Rethrows the original axios error so the caller can detect the
  // LISTING_CREDIT_REQUIRED (402) response and prompt to buy credits.
  publish: async ({ commit }, id) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUpdating: true });
    try {
      const response = await CarListingsAPI.publish(id);
      commit(types.EDIT_WGPT_CAR_LISTING, response.data);
      return response.data;
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUpdating: false });
    }
  },
  unpublish: async ({ commit }, { id, status }) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUpdating: true });
    try {
      const response = await CarListingsAPI.unpublish(id, status);
      commit(types.EDIT_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isDeleting: true });
    try {
      await CarListingsAPI.delete(id);
      commit(types.DELETE_WGPT_CAR_LISTING, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isDeleting: false });
    }
  },
  attachPhoto: async ({ commit, rootGetters }, { id, slot, file }) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUploadingPhoto: true });
    try {
      const payload = await uploadPayload(id, file, rootGetters);
      const response = await CarListingsAPI.attachPhoto(id, slot, payload);
      commit(types.EDIT_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUploadingPhoto: false });
    }
    return null;
  },
  removePhoto: async ({ commit }, { id, slot }) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isRemovingPhoto: true });
    try {
      const response = await CarListingsAPI.removePhoto(id, slot);
      commit(types.EDIT_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isRemovingPhoto: false });
    }
    return null;
  },
  // Rethrows the raw axios error so the caller can detect the
  // PHOTO_GENERATION_LIMIT_REACHED (422) response.
  regeneratePhoto: async ({ commit }, { id, slot }) => {
    const response = await CarListingsAPI.regeneratePhoto(id, slot);
    commit(types.EDIT_WGPT_CAR_LISTING, response.data);
    return response.data;
  },
  // Quiet re-fetch used to poll while AI photo generations are pending; no
  // uiFlags so polling doesn't flash the editor's loading state.
  refresh: async ({ commit }, id) => {
    const response = await CarListingsAPI.show(id);
    commit(types.EDIT_WGPT_CAR_LISTING, response.data);
    return response.data;
  },
  attachVideo: async ({ commit, rootGetters }, { id, file }) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUploadingVideo: true });
    try {
      const payload = await uploadPayload(id, file, rootGetters);
      const response = await CarListingsAPI.attachVideo(id, payload);
      commit(types.EDIT_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUploadingVideo: false });
    }
    return null;
  },
  removeVideo: async ({ commit }, { id }) => {
    commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUploadingVideo: true });
    try {
      const response = await CarListingsAPI.removeVideo(id);
      commit(types.EDIT_WGPT_CAR_LISTING, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_LISTING_UI_FLAG, { isUploadingVideo: false });
    }
    return null;
  },
};

export const mutations = {
  [types.SET_WGPT_CAR_LISTING_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_WGPT_CAR_LISTINGS]: MutationHelpers.set,
  [types.APPEND_WGPT_CAR_LISTINGS]: ($state, data) => {
    $state.records.push(...data);
  },
  [types.SET_WGPT_CAR_LISTING_META]: ($state, data) => {
    $state.meta = { ...$state.meta, ...data };
  },
  [types.ADD_WGPT_CAR_LISTING]: ($state, data) => {
    const recordIndex = $state.records.findIndex(
      record => record.id === data.id
    );
    if (recordIndex > -1) {
      $state.records[recordIndex] = data;
    } else {
      $state.records.unshift(data);
    }
  },
  [types.EDIT_WGPT_CAR_LISTING]: MutationHelpers.update,
  [types.DELETE_WGPT_CAR_LISTING]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
